import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CompareContext = createContext();

const MAX_COMPARE = 4;

export const CompareProvider = ({ children }) => {
  const [compareList, setCompareList] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('compareList');
    if (saved) {
      try {
        setCompareList(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load compare list:', error);
      }
    }
  }, []);

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('compareList', JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (policy, isUserPolicy = false) => {
    if (compareList.length >= MAX_COMPARE) {
      toast.warning('Comparison limit reached', {
        description: `You can compare up to ${MAX_COMPARE} policies at once`
      });
      return false;
    }

    if (compareList.find(p => p.id === policy.id && p.isUserPolicy === isUserPolicy)) {
      toast.info('Policy already in comparison', {
        description: 'This policy is already added to your comparison list'
      });
      return false;
    }

    // Mark if it's a user policy or marketplace policy
    const policyWithType = { ...policy, isUserPolicy };
    
    setCompareList([...compareList, policyWithType]);
    toast.success('Policy added to comparison', {
      description: `${policy.name || policy.insurer_name} has been added to compare list`
    });
    return true;
  };

  const removeFromCompare = (policyId, isUserPolicy = false) => {
    setCompareList(compareList.filter(p => !(p.id === policyId && p.isUserPolicy === isUserPolicy)));
    toast.success('Policy removed from comparison');
  };

  const clearCompare = () => {
    setCompareList([]);
    toast.success('Comparison list cleared');
  };

  const isInCompare = (policyId, isUserPolicy = false) => {
    return compareList.some(p => p.id === policyId && p.isUserPolicy === isUserPolicy);
  };

  return (
    <CompareContext.Provider value={{
      compareList,
      addToCompare,
      removeFromCompare,
      clearCompare,
      isInCompare,
      canAddMore: compareList.length < MAX_COMPARE
    }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
